<template>
   <div v-if="data.sections.length > 0" data-cy="informed-consent-block">
      <div
         v-for="(section, index) in data.sections"
         :key="index"
         class="pb-6"
         :data-cy="`consent-section-${section.id}`"
      >
         <!-- section rich-text content -->
         <RichTextRenderer class="pb-4" :document="section.content" />

         <!-- section single choices -->
         <div v-for="(choice, cIndex) in section.choices" :key="`${section.id}-${choice.value}`">
            <div class="radio-block">
               <div
                  class="radio-block-bg mb-3 flex min-h-12 w-full items-center rounded-md"
                  :class="[choice.value === section.selectedChoiceValue ? 'bg-brand-dark text-white ' : 'bg-grey-10']"
               >
                  <input
                     :id="`consent-choice-item-${section.id}-${cIndex}`"
                     :data-cy="`consent-choice-item-${section.id}-${choice.value}`"
                     type="radio"
                     :name="section.id"
                     :value="choice.value"
                     class="absolute right-0 z-1 mr-6"
                     :data-test="section.selectedChoiceValue"
                     :checked="choice.value === section.selectedChoiceValue"
                     :disabled="!isConsentEditable"
                     @change="updateChoice(section.id, choice.value)"
                  />
                  <label
                     :for="`consent-choice-item-${section.id}-${cIndex}`"
                     class="radio-block-label flex size-full items-center rounded-md"
                     :class="[isConsentEditable ? 'cursor-pointer' : 'cursor-default']"
                  >
                     <span class="py-4 pl-6 pr-16">{{ choice.label }}</span>
                  </label>
               </div>
            </div>
         </div>
      </div>
      <InvalidInputsList :errorList="errorList" :class="{ hidden: !showBlockErrors }" />
   </div>
</template>

<script setup lang="ts">
import { useI18n } from "@/vue-i18n/vue-i18n";
import { toRaw } from "vue";
import type { AnswerResp } from "@/data/api-layer/answersApi";
import { keysToSnake, toCamelCase } from "@/js/helpers/object.helper";
import type { RichText } from "@/types";
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";

export interface InformedConsentAnswerData {
   consent: {
      sections: {
         // Example: sample_collection: "true"
         [sectionId: string]: string | null;
      };
      // signature_base64: string;
   };
}

export interface InformedContentData {
   sections: {
      id: string;
      content: RichText;
      choices: {
         value: string;
         label: string;
      }[];
   }[];
}
export interface InformedContentBlock extends FlowselBlock {
   data: InformedContentData;
}

const { t } = useI18n();

const emit = defineEmits(["update-valid", "update-answer"]);

const props = defineProps<{
   userBlockAnswer: AnswerResp | null;
   block: InformedContentBlock;
   showBlockErrors: boolean;
}>();

// data
const data = reactive({
   // 1 - Remove reactivity from the prop (the prop should never change)
   // 2 - For each consent section, add a "selectedChoiceValue"
   // 3 - Convert each choice value to a string (they are stored in the database as booleans)
   sections: toRaw(props.block.data.sections).map((section) => {
      const sectionId = toCamelCase(section.id); // example for section.id: "agree_to_take_part"

      const choiceValue = props.userBlockAnswer?.data?.consent?.sections[sectionId];
      const userSectionChoiceValue = choiceValue !== undefined ? choiceValue : null;

      return {
         ...section,
         selectedChoiceValue: userSectionChoiceValue?.toString(),
         choices: section.choices.map((choice) => {
            return {
               ...choice,
               value: choice.value.toString(),
            };
         }),
      };
   }),
});

// computed
//-----------------------------------------
const isConsentEditable = computed<boolean>(() => {
   // if the user has already answered the consent block, then the consent is not editable
   return !props.userBlockAnswer?.data;
});

// has a consent been selected for each section?
const allSectionsCompleted = computed<boolean>(() => {
   // check if all sections have a selected choice
   return data.sections.every((section) => {
      return section.selectedChoiceValue;
   });
});

const errorList = computed(() => {
   if (!allSectionsCompleted.value)
      return [
         {
            inputId: props.block.id,
            validator: "all_required",
            message: t("validation.choice.required_all_sections"),
         },
      ];
   return [];
});

// methods
//-----------------------------------------
function updateChoice(sectionId, choiceValue) {
   const sectionToUpdate = data.sections.find((section) => section.id === sectionId);
   if (sectionToUpdate) {
      sectionToUpdate.selectedChoiceValue = choiceValue;
      processInputs();
   }
}

function processInputs() {
   if (!allSectionsCompleted.value) {
      // an input is invalid or has errors
      emit("update-valid", false);
   } else {
      // all inputs are valid
      const sectionAnswers = data.sections.reduce((acc, section) => {
         acc[section.id] = section.selectedChoiceValue === "true"; // convert string value to boolean primitive
         return acc;
      }, {});

      emit("update-valid", true);
      emit("update-answer", {
         consent: {
            sections: keysToSnake(sectionAnswers),
         },
      });
   }
}

processInputs();
</script>

<style scoped lang="scss">
.radio-block .radio-block-bg input {
   accent-color: white;
}
.radio-block .radio-block-bg input:checked {
   background: var(--colour-brand-background);

   accent-color: white;
}
.radio-block .radio-block-bg input:checked + .radio-block-label {
   background: var(--colour-brand-background);
   color: white;
}
</style>
